import {
  FOLLOW_UP_REASON_LABEL,
  FOLLOW_UP_STATE_LABEL,
  type FollowUp,
  type FollowUpReason,
  type FollowUpState,
} from '@cyberscore/contracts';
import { BookOpen, Check, MessageCircleQuestion, Sparkles, Undo2, X } from 'lucide-react';
import { useToast } from '../../ui/Toasts';
import { useDecideFollowUp, useFollowUps, useProposeFollowUps } from './api';

const REASON_TONE: Record<FollowUpReason, string> = {
  contradicts_document: 'bad',
  contradicts_answer: 'bad',
  names_risk: 'warn',
  vague: 'neutral',
};
const STATE_TONE: Record<FollowUpState, string> = {
  open: 'neutral',
  answered: 'warn',
  confirmed: 'ok',
  kept: 'ok',
  dismissed: 'neutral',
};

/**
 * For underwriters (task 87): the follow-up questions the assistant asked the business, why, the control
 * library entries behind each, the business's reply and the answer it supports. Nothing changes an answer
 * until someone here confirms it.
 */
export function FollowUpsCard({ id, canWrite }: { id: string; canWrite: boolean }) {
  const list = useFollowUps(id);
  const propose = useProposeFollowUps(id);
  const toast = useToast();
  const items = list.data ?? [];
  const waiting = items.filter((f) => f.state === 'answered').length;

  return (
    <section className="card pad" aria-labelledby={`follow-ups-${id}`}>
      <div className="row-between">
        <h2 id={`follow-ups-${id}`} className="section-title row gap">
          <MessageCircleQuestion size={17} /> Follow-up questions
        </h2>
        {canWrite && (
          <button
            type="button"
            className="btn btn-sm"
            disabled={propose.isPending}
            onClick={async () => {
              try {
                const r = await propose.mutateAsync();
                if (r.failed) toast('The assistant couldn’t be reached; try again later', 'bad');
                else toast(r.proposed ? `${r.proposed} follow-up ${r.proposed === 1 ? 'question' : 'questions'} sent to the business` : 'Nothing new to follow up', 'ok'); // prettier-ignore
              } catch (err) {
                toast(err instanceof Error ? err.message : 'Could not look for follow-ups', 'bad');
              }
            }}
          >
            <Sparkles size={14} /> {propose.isPending ? 'Looking…' : 'Look for follow-ups'}
          </button>
        )}
      </div>
      <p className="muted small">
        {items.length
          ? `${waiting ? `${waiting} ${waiting === 1 ? 'reply is' : 'replies are'} waiting for you. ` : ''}The assistant asks where an answer is vague, contradicts another answer or a document, or names a risk. A reply changes an answer only when you confirm it.` // prettier-ignore
          : 'None yet. The assistant looks when the business submits: vague answers, contradictions and risks the business mentions.'}
      </p>
      {items.length > 0 && (
        <ol className="review-list">
          {items.map((f) => (
            <Item key={f.id} id={id} f={f} canWrite={canWrite} />
          ))}
        </ol>
      )}
    </section>
  );
}

function Item({ id, f, canWrite }: { id: string; f: FollowUp; canWrite: boolean }) {
  const decide = useDecideFollowUp(id);
  const toast = useToast();
  const act = async (action: 'confirm' | 'keep' | 'dismiss', ok: string) => {
    try {
      await decide.mutateAsync({ fid: f.id, action });
      toast(ok, 'ok');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not save that', 'bad');
    }
  };
  return (
    <li className="card pad stack-sm" aria-label={`Follow-up on ${f.question_key}`}>
      <div className="row gap wrap">
        <span className={`pill ${REASON_TONE[f.reason]}`}>{FOLLOW_UP_REASON_LABEL[f.reason]}</span>
        <span className={`pill ${STATE_TONE[f.state]}`}>{FOLLOW_UP_STATE_LABEL[f.state]}</span>
        <span className="muted small mono">{f.question_key}</span>
      </div>
      <div className="qtext">{f.question_text}</div>
      <p className="small muted">
        Answer: {f.answer_label}
        {f.related && ` · against ${f.related}`}
      </p>
      {f.quote && <p className="small muted">From their words: “{f.quote}”</p>}
      <p className="small insurer-says">“{f.prompt}”</p>
      {f.grounding.length > 0 && (
        <p className="small muted row gap wrap">
          <BookOpen size={13} aria-hidden /> {f.grounding.map((g) => `${g.control} ${g.name}`).join(' · ')}
        </p>
      )}
      {f.reply && (
        <p className="small">
          <b>They replied:</b> {f.reply.text}
        </p>
      )}
      {f.state === 'answered' && (
        <p className="small muted">
          {f.proposal
            ? `The reply supports changing ${f.proposal.question_key} to “${f.proposal.label}” (${Math.round(f.proposal.confidence * 100)}% sure${f.proposal.quote ? `: “${f.proposal.quote}”` : ''}).` // prettier-ignore
            : 'The reply doesn’t support a different answer.'}
        </p>
      )}
      {f.decision?.note && <p className="small muted">Note: {f.decision.note}</p>}
      {canWrite && f.state === 'open' && (
        <div className="row gap wrap">
          <button type="button" className="btn btn-sm btn-ghost" disabled={decide.isPending} onClick={() => act('dismiss', 'Follow-up withdrawn')}>
            <X size={13} /> Withdraw
          </button>
        </div>
      )}
      {canWrite && f.state === 'answered' && (
        <div className="row gap wrap">
          {f.proposal && (
            <button type="button" className="btn btn-sm btn-primary" disabled={decide.isPending} onClick={() => act('confirm', 'Answer changed')}>
              <Check size={13} /> Confirm change
            </button>
          )}
          <button type="button" className="btn btn-sm" disabled={decide.isPending} onClick={() => act('keep', 'Answer kept')}>
            <Undo2 size={13} /> Keep answer
          </button>
        </div>
      )}
    </li>
  ); // prettier-ignore
}
